import { browserStatics } from "@/src/shared/BrowserStatic";

import { urlFormat } from "./Helpers";

export enum AjaxMethod {
    Get,
    Post,
}

export type AjaxSuccess<TResult extends object | string | boolean | number | void> = (
    response: TResult,
    xhr?: XMLHttpRequest,
) => void;

export type AjaxFail = (response: any, xhr?: XMLHttpRequest) => boolean;

export interface IAjax {
    ajaxAction?: string;
    ajaxMethod?: AjaxMethod;
}

export class Ajax<TResult extends object | string | boolean | number | void> implements IAjax {
    public ajaxAction: string | undefined;

    public ajaxMethod: AjaxMethod;

    public success: AjaxSuccess<TResult> | undefined;

    public fail: AjaxFail | undefined;

    constructor(props: IAjax) {
        this.ajaxAction = props.ajaxAction;
        this.ajaxMethod = props.ajaxMethod || AjaxMethod.Get;
    }

    public request = (urlParams?: object, postParams?: object): XMLHttpRequest | undefined => {
        const url = urlFormat(this.ajaxAction, urlParams);

        if (!url) {
            return undefined;
        }

        switch (this.ajaxMethod) {
            case AjaxMethod.Post:
                return this.post(url, postParams as { [key: string]: string });
            default:
                return this.get(url);
        }
    };

    private handleSuccess = (xhr: XMLHttpRequest): void => {
        if (!this.success) {
            return;
        }

        const response: TResult = Ajax.parseResponse(xhr) as TResult;

        this.success(response, xhr);
    };

    private handleFail = (xhr: XMLHttpRequest): void => {
        if (!this.fail) {
            return;
        }

        const response: TResult = Ajax.parseResponse(xhr) as TResult;

        this.fail(response, xhr);
    };

    private handleTimeout = (xhr: XMLHttpRequest): void => {
        if (!this.fail) {
            return;
        }

        this.fail("timeout", xhr);
    };

    private get(url: string): XMLHttpRequest {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        xhr.setRequestHeader("Accept-Images", browserStatics.imageSupport);

        this.requestOnLoad(xhr);
        xhr.send();
        return xhr;
    }

    private post = (url: string, params?: { [index: string]: string }): XMLHttpRequest => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        xhr.setRequestHeader("Accept-Images", browserStatics.imageSupport);

        let urlEncodedData: string | null = null;
        xhr.setRequestHeader("Content-Type", "application/json; charset=utf-8");

        if (params) {
            urlEncodedData = JSON.stringify(params);
        }

        this.requestOnLoad(xhr);

        xhr.send(urlEncodedData);
        return xhr;
    };

    private requestOnLoad = (xhr: XMLHttpRequest): void => {
        if (!xhr) {
            return;
        }

        xhr.onload = () => (xhr.status === 200 ? this.handleSuccess(xhr) : this.handleFail(xhr));

        xhr.ontimeout = () => this.handleTimeout(xhr);
        xhr.onerror = () => this.handleFail(xhr);
    };

    private static parseResponse = <TResponse>(xhr: XMLHttpRequest): TResponse | null => {
        if (!xhr || !xhr.responseText) {
            return null;
        }

        try {
            return JSON.parse(xhr.responseText);
        } catch (e) {
            return xhr.responseText as TResponse;
        }
    };
}
